/**
 * Rows of the IoT commissioning checklist (IBM-1045), derived from the
 * backend's CREATE form of the `ASSET_IOT_COMMISSIONING` event so the
 * frontend never hard-codes the five check indexes: every bool / checkbox
 * attribute becomes one check row (in backend order), the first free-text
 * attribute becomes the message field. `mustAccept` mirrors the
 * backend's `must_accept` rule — the checklist refuses to submit without such a
 * check ticked.
 */
export interface CommissioningAttribute {
    index: string;
    name: string;
    hint?: string | null;
    type?: string | null;
    inputType?: string | null;
    rules?: Array<{ name: string; message?: string | null } | null> | null;
}

export type CommissioningStep =
    | { kind: 'check'; attribute: CommissioningAttribute; mustAccept: boolean }
    | { kind: 'message'; attribute: CommissioningAttribute };

const isCheck = (attribute: CommissioningAttribute): boolean =>
    attribute.type === 'bool' || attribute.type === 'boolean' || attribute.inputType === 'checkbox';

const isText = (attribute: CommissioningAttribute): boolean =>
    !isCheck(attribute) && (attribute.inputType === 'textarea' || attribute.type === 'string');

export const deriveCommissioningSteps = (attributes: ReadonlyArray<CommissioningAttribute> | null | undefined): CommissioningStep[] => {
    const list = attributes ?? [];
    const checks: CommissioningStep[] = list.filter(isCheck).map((attribute) => ({
        kind: 'check',
        attribute,
        mustAccept: (attribute.rules ?? []).some((rule) => rule?.name === 'must_accept'),
    }));
    const message = list.find(isText);
    return message ? [...checks, { kind: 'message', attribute: message }] : checks;
};
